import { create } from 'zustand';
import { getState, useState } from '@/framework';
import { IExportState, StateType } from '@/version-types';

/** 状态 */
export interface IState {
    /** 是否在裁剪 */
    isCropStage: boolean;
    // 是否正在执行舞台裁剪操作
    isCropStageOperating: boolean;
    isStageAutoCrop: boolean;
    currentCropStageScale: number;
    isOutOfStage: boolean;
    operateBaseInfo: {
        width: number;
        height: number;
        translateX: number;
        translateY: number;
    };
}

/** 动作 */
export interface IAction {
    /** 设置是否在裁剪 */
    setIsCropStage: (isCropStage: boolean) => void;
    destroy: () => void;
    setIsCropStageOperating: (isCropStageOperating: boolean) => void;
    setIsStageAutoCrop: (isStageAutoCrop: boolean) => void;
    setCurrentCropStageScale: (currentCropStageScale: number) => void;
    setIsOutOfStage: (isOutOfStage: boolean) => void;
    setOperateBaseInfo: (operateBaseInfo: {
        width: number;
        height: number;
        translateX: number;
        translateY: number;
    }) => void;
}

/** 存储类型 */
export interface IStore extends IState, IAction {}

/** 初始化状态 */
function getInitialState(): IState {
    return {
        isCropStage: false,
        isCropStageOperating: false,
        isStageAutoCrop: true,
        currentCropStageScale: 1,
        isOutOfStage: false,
        operateBaseInfo: {
            width: 0,
            height: 0,
            translateX: 0,
            translateY: 0,
        },
    };
}

/** 创建存储 */
const createStore = (identity: string) => {
    const store = create<IStore>((set, get) => ({
        ...getInitialState(),
        setIsCropStage: (isCropStage) => {
            set({ isCropStage });
        },
        setIsCropStageOperating: (isCropStageOperating) => {
            set({ isCropStageOperating });
        },
        setIsStageAutoCrop: (isStageAutoCrop) => {
            set({ isStageAutoCrop });
        },
        setCurrentCropStageScale: (currentCropStageScale) => {
            set({ currentCropStageScale });
        },
        destroy() {
            set(getInitialState());
        },
        setIsOutOfStage: (isOutOfStage) => {
            set({ isOutOfStage });
        },
        setOperateBaseInfo: (operateBaseInfo) => {
            set({ operateBaseInfo });
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'crop-stage-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useStageCropStore = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getStageCropStore = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

export const watchStageCropStore = (identity: string, callback: (state: IStore, preState: IStore) => void) => {
    return getState<ModuleType>(identity)[name].subscribe((state, preState) => {
        if (JSON.stringify(preState) !== JSON.stringify(state)) {
            callback(state, preState);
        }
    });
};

const exportState: IExportState = {
    name,
    createState: createStore,
};

export default exportState;
